import {FieldType} from '../../../models/field-type';
import {FormConfig} from '../../../models/form-config';
import {VehicleModelConsts} from '../../../../core/consts/vehicle-model.consts';
import {VehicleModelStateService} from '../../../../core/services/impl/section-services/vehicle-model/vehicle-model-state.service';
import {SelectContentText} from '../../../components/fields/multiselect/material-multiselect.component';
import {BehaviorSubject} from 'rxjs/Rx';
import {PredefinedValuesService} from '../../../../core/services/impl/section-services/predefined-values/predefined-values.service';
import {today} from '../../../utils/time-utils';
import {Validator} from '../../../models/validator.enum';
import {InsuranceDataEnum} from '../../../../core/consts/insurance-data.enum';
import {addDaysToDate, changeDateByYears, isMobile} from '../../../../core/utils/utils';
import {FieldConfig, FieldNameSource} from '../../../models/field-config';
import {EventEmitter} from '@angular/core';
import {SelectDialogMode} from '../../../models/select-dialog-mode.enum';
import {MaterialDatepickerType} from '../../../enums/material-datepicker-type.enum';
import {DatepickerStartView} from '../../../enums/datepicker-start-view.enum';

export class VehicleModelSectionConfig {

  private readonly START_INSURANCE_DATE_FIELD_CONFIG = {
    fieldKey: InsuranceDataEnum.START_INSURANCE_DATE,
    caption: 'VEHICLE_MODEL_SECTION.FORM_FIELDS.startInsuranceDate',
    type: FieldType.DateField,
    validators: [Validator.REQUIRED],
    displayInEditableMode: true,
    materialDateFieldConfig: {
      minDate: today(),
      maxDate: addDaysToDate(new Date(), 180),
      startView: DatepickerStartView.YEAR,
    }
  };

  private readonly END_INSURANCE_DATE_FIELD_CONFIG = {
    fieldKey: InsuranceDataEnum.END_INSURANCE_DATE,
    caption: 'VEHICLE_MODEL_SECTION.FORM_FIELDS.endInsuranceDate',
    type: FieldType.DateField,
    validators: [Validator.REQUIRED],
    displayInEditableMode: true,
    materialDateFieldConfig: {
      minDate: changeDateByYears(new Date(), -1),
      maxDate: changeDateByYears(new Date(), 1),
      startView: DatepickerStartView.YEAR,
    }
  };

  constructor(private stateService: VehicleModelStateService,
              private predefined: PredefinedValuesService) {
  }

  createVehicleModelConfig(): FormConfig {
    const formConfig = <FormConfig> {
      formKey: 'VEHICLE_MODEL_SECTION',
      fieldsConfig: [
        {
          fieldKey: VehicleModelConsts.BRAND_CODE,
          caption: `VEHICLE_MODEL_SECTION.FORM_FIELDS.${VehicleModelConsts.BRAND_CODE}`,
          type: FieldType.Select,
          displayInPreviewMode: true,
          validators: [Validator.REQUIRED],
          options: {
            item: 'brandCode',
            label: {label: 'brandName'},
            items: this.stateService.config[VehicleModelConsts.BRAND_CODE].dictionary
          },
          select: {
            contentTextType: SelectContentText.CAPTION_TEXT,
            dropdownPosition: 'bottom',
            multiple: false,
            loadingEvent: new BehaviorSubject(true),
            valueSelectedEvent: new EventEmitter<any>(),
            searchable: true,
            valueBasedInitialDictionary: true,
            typeToSearchText: `VEHICLE_MODEL_SECTION.TYPE_TO_SEARCH.${VehicleModelConsts.BRAND_CODE}`,
            autoSelectOnSearch: true,
            sharedSelectDialog: true,
          },
        },
        {
          fieldKey: VehicleModelConsts.MODEL_CODE,
          caption: `VEHICLE_MODEL_SECTION.FORM_FIELDS.${VehicleModelConsts.MODEL_CODE}`,
          type: FieldType.Select,
          validators: [Validator.REQUIRED],
          displayInPreviewMode: true,
          options: {
            item: 'modelCode',
            template: VehicleModelConsts.MODEL_CODE,
            label: {
              label: ['modelName', 'modelName2'],
              labelConfig: {
                bindLabel: 'modelName'
              }
            },
            items: this.stateService.config[VehicleModelConsts.MODEL_CODE].dictionary
          },
          select: {
            contentTextType: SelectContentText.CAPTION_TEXT,
            dropdownPosition: 'bottom',
            multiple: false,
            loadingEvent: new BehaviorSubject(true),
            valueSelectedEvent: new EventEmitter<any>(),
            setValueIfOnlyOneItem: true,
            searchable: true,
            valueBasedInitialDictionary: true,
            mobileSelectPanelClass: 'vehicle-model-section__select-with-custom-template__mobile-select',
            typeToSearchText: `VEHICLE_MODEL_SECTION.TYPE_TO_SEARCH.${VehicleModelConsts.MODEL_CODE}`,
            autoSelectOnSearch: true,
            sharedSelectDialog: true,
          },
        },
        {
          fieldKey: VehicleModelConsts.FUEL_CODE,
          caption: `VEHICLE_MODEL_SECTION.FORM_FIELDS.${VehicleModelConsts.FUEL_CODE}`,
          type: FieldType.Select,
          validators: [Validator.REQUIRED],
          displayInPreviewMode: true,
          options: {
            item: 'fuelCode',
            label: {label: 'fuelName'},
            items: this.stateService.config[VehicleModelConsts.FUEL_CODE].dictionary
          },
          select: {
            contentTextType: SelectContentText.CAPTION_TEXT,
            dropdownPosition: 'bottom',
            multiple: false,
            loadingEvent: new BehaviorSubject(true),
            valueSelectedEvent: new EventEmitter<any>(),
            setValueIfOnlyOneItem: true,
            searchable: true,
            autoSelectOnSearch: true,
            sharedSelectDialog: true,
          },
        },
        {
          fieldKey: VehicleModelConsts.ENGINE_CAPACITY,
          caption: `VEHICLE_MODEL_SECTION.FORM_FIELDS.${VehicleModelConsts.ENGINE_CAPACITY}`,
          type: FieldType.Select,
          validators: [Validator.REQUIRED],
          displayInPreviewMode: true,
          options: {
            label: {
              labelConfig: {
                suffix: ' cm3',
                options: {
                  removeTailsAfter: ','
                }
              },
            },
            items: this.stateService.config[VehicleModelConsts.ENGINE_CAPACITY].dictionary
          },
          select: {
            contentTextType: SelectContentText.CAPTION_TEXT,
            dropdownPosition: 'bottom',
            multiple: false,
            loadingEvent: new BehaviorSubject(true),
            valueSelectedEvent: new EventEmitter<any>(),
            setValueIfOnlyOneItem: true,
            valueBasedInitialDictionary: true,
            searchable: true,
            autoSelectOnSearch: true,
            sharedSelectDialog: true,
          },
        },
        {
          fieldKey: VehicleModelConsts.ENGINE_POWER,
          caption: `VEHICLE_MODEL_SECTION.FORM_FIELDS.${VehicleModelConsts.ENGINE_POWER}`,
          type: FieldType.Select,
          options: {
            label: {
              labelConfig: {
                suffix: ' KM',
                options: {
                  removeTailsAfter: ','
                }
              },
            },
            items: []
          },
          select: {
            contentTextType: SelectContentText.CAPTION_TEXT,
            dropdownPosition: 'bottom',
            multiple: false,
            loadingEvent: new BehaviorSubject(true),
            valueSelectedEvent: new EventEmitter<any>(),
            setValueIfOnlyOneItem: true,
            valueBasedInitialDictionary: true,
            searchable: true,
            autoSelectOnSearch: true,
            sharedSelectDialog: true,
          },
        },
        // {
        //   fieldKey: VehicleModelConsts.NUMBER_OF_DOORS,
        //   caption: `VEHICLE_MODEL_SECTION.FORM_FIELDS.${VehicleModelConsts.NUMBER_OF_DOORS}`,
        //   type: FieldType.Select,
        //   validators: [Validator.REQUIRED],
        //   options: {
        //     items: this.stateService.config[VehicleModelConsts.NUMBER_OF_DOORS].dictionary
        //   },
        //   select: {
        //     contentTextType: SelectContentText.CAPTION_TEXT,
        //     dropdownPosition: 'bottom',
        //     multiple: false,
        //     loadingEvent: new BehaviorSubject(null),
        //     setValueIfOnlyOneItem: true
        //   },
        // },
        {
          fieldKey: VehicleModelConsts.VEHICLE_CODE_EUROTAX,
          caption: `VEHICLE_MODEL_SECTION.FORM_FIELDS.${VehicleModelConsts.VEHICLE_CODE_EUROTAX}`,
          type: FieldType.Select,
          description: 'VEHICLE_MODEL_SECTION.TOOLTIP.eurotax',
          validators: [Validator.REQUIRED],
          displayInPreviewMode: true,
          options: {
            item: 'code',
            template: VehicleModelConsts.VEHICLE_CODE_EUROTAX,
            label: {
              label: ['name', 'engineCapacity', 'kilowatts', 'horsepower', 'numberOfDoors', 'bodyType'],
              labelConfig: {
                bindLabel: 'name',
                suffixes: {
                  horsepower: 'KM',
                  kilowatts: 'kW',
                  engineCapacity: 'cm3',
                  numberOfDoors: ' drzwi'
                },
                options: {
                  removeTailsAfter: ',',
                  separateBy: ' / '
                }
              },
            },
            items: this.stateService.config[VehicleModelConsts.VEHICLE_CODE_EUROTAX].dictionary
          },
          select: {
            contentTextType: SelectContentText.CAPTION_TEXT,
            dropdownPosition: 'bottom',
            multiple: false,
            loadingEvent: new BehaviorSubject(true),
            valueSelectedEvent: new EventEmitter<any>(),
            setValueIfOnlyOneItem: true,
            searchable: true,
            mobileSelectPanelClass: 'vehicle-model-section__select-with-custom-template__mobile-select',
            typeToSearchText: `VEHICLE_MODEL_SECTION.TYPE_TO_SEARCH.${VehicleModelConsts.VEHICLE_CODE_EUROTAX}`,
            autoSelectOnSearch: true,
            sharedSelectDialog: true,
            lastFieldInSharedSelectDialog: !this.stateService.isLoggedUserIdParam
          }
        },
        {
          fieldKey: VehicleModelConsts.VEHICLE_CODE_INFOEKSPERT,
          caption: `VEHICLE_MODEL_SECTION.FORM_FIELDS.${VehicleModelConsts.VEHICLE_CODE_INFOEKSPERT}`,
          type: FieldType.Select,
          displayInPreviewMode: false,
          description: 'VEHICLE_MODEL_SECTION.TOOLTIP.infoExpert',
          // validators: [Validator.REQUIRED],
          options: {
            item: 'code',
            template: VehicleModelConsts.VEHICLE_CODE_INFOEKSPERT,
            label: {
              label: ['name', 'engineCapacity', 'kilowatts', 'horsepower', 'numberOfDoors', 'bodyType'],
              labelConfig: {
                bindLabel: 'name',
                suffixes: {
                  horsepower: 'KM',
                  kilowatts: 'kW',
                  engineCapacity: 'cm3',
                  numberOfDoors: ' drzwi'
                },
                options: {
                  removeTailsAfter: ',',
                  separateBy: ' / '
                }
              },
            },
            items: this.stateService.config[VehicleModelConsts.VEHICLE_CODE_INFOEKSPERT].dictionary
          },
          select: {
            contentTextType: SelectContentText.CAPTION_TEXT,
            dropdownPosition: 'bottom',
            multiple: false,
            loadingEvent: new BehaviorSubject(true),
            valueSelectedEvent: new EventEmitter<any>(),
            setValueIfOnlyOneItem: true,
            searchable: true,
            mobileSelectPanelClass: 'vehicle-model-section__select-with-custom-template__mobile-select',
            autoSelectOnSearch: true,
            sharedSelectDialog: true,
            lastFieldInSharedSelectDialog: true,
          }
        },
      ]
    };

    (<any[]>formConfig.fieldsConfig).push(this.stateService.isDiagnosticStationContext ? this.END_INSURANCE_DATE_FIELD_CONFIG : this.START_INSURANCE_DATE_FIELD_CONFIG);

    const brandCodeIndex = formConfig.fieldsConfig.findIndex(fieldConfig => fieldConfig.fieldKey === VehicleModelConsts.BRAND_CODE);
    formConfig.fieldsConfig.splice(brandCodeIndex + 1, 0, this.createProductionYearFieldConfig());

    formConfig.fieldsConfig.forEach(fieldConfig => {
      this.predefined.addPredefinedValue(
        fieldConfig,
        this.predefined.values,
        formConfig.formKey,
        fieldConfig.fieldKey
      )
    });

    return formConfig;
  }

  private createProductionYearFieldConfig(): FieldConfig {
    if (isMobile()) {
      return {
        fieldKey: VehicleModelConsts.PRODUCTION_YEAR,
        caption: `VEHICLE_MODEL_SECTION.FORM_FIELDS.${VehicleModelConsts.PRODUCTION_YEAR}`,
        type: FieldType.Select,
        validators: [Validator.REQUIRED],
        displayInPreviewMode: true,
        options: {
          items: this.stateService.config[VehicleModelConsts.PRODUCTION_YEAR].dictionary
        },
        select: {
          contentTextType: SelectContentText.CAPTION_TEXT,
          dropdownPosition: 'bottom',
          multiple: false,
          loadingEvent: new BehaviorSubject(true),
          valueSelectedEvent: new EventEmitter<any>(),
          setValueIfOnlyOneItem: true,
          searchable: true,
          valueBasedInitialDictionary: true,
          sharedSelectDialog: true,
          dialogMode: SelectDialogMode.DATEPICKER,
        },
      };
    } else {
      return {
        fieldKey: VehicleModelConsts.PRODUCTION_YEAR,
        caption: `VEHICLE_MODEL_SECTION.FORM_FIELDS.${VehicleModelConsts.PRODUCTION_YEAR}`,
        type: FieldType.DateField,
        validators: [Validator.REQUIRED],
        displayInEditableMode: true,
        materialDateFieldConfig: {
          startView: DatepickerStartView.MULTI_YEAR,
          type: MaterialDatepickerType.YEAR,
          datepickerToggleIcon: 'arrow_drop_down',
          hideCalendarPeriodButton: true,
          initMinDateWithStartAt: false,
          async: true,
          loadingEvent: new BehaviorSubject(true),
          manualInputAllowed: true,
          readonly: false,
          typeToSearchText: `VEHICLE_MODEL_SECTION.TYPE_TO_SEARCH.${VehicleModelConsts.PRODUCTION_YEAR}`
        }
      };
    }
  }
}
